import type { Ref } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import Lozenge from '@atlaskit/lozenge';
import { Inline } from '@atlaskit/primitives/compiled';

import { messages } from './messages';

export const ASSESSMENT_RESULTS_STATUS_FILTER = {
	PASSED: 'PASSED',
	NEEDS_ATTENTION: 'NEEDS_ATTENTION',
	SKIPPED: 'SKIPPED',
} as const;

export type AssessmentResultsStatusFilterValue =
	(typeof ASSESSMENT_RESULTS_STATUS_FILTER)[keyof typeof ASSESSMENT_RESULTS_STATUS_FILTER];

type AssessmentResultsStatusFilterProps = {
	value: AssessmentResultsStatusFilterValue | null;
	onChange: (value: AssessmentResultsStatusFilterValue | null) => void;
};

export const AssessmentResultsStatusFilter = ({
	value,
	onChange,
}: AssessmentResultsStatusFilterProps) => {
	const { formatMessage } = useIntl();

	return (
		<Inline space="space.100" alignBlock="center">
			<DropdownMenu
				label={formatMessage(messages.statusFilterMenu)}
				spacing="compact"
				shouldRenderToParent
				// This is Atlaskit's recommended pattern for custom trigger components.
				// eslint-disable-next-line react/no-unstable-nested-components
				trigger={({ triggerRef, ...triggerProps }) => (
					<Button
						ref={triggerRef as Ref<HTMLButtonElement>}
						{...triggerProps}
						isSelected={value !== null}
						iconAfter={ChevronDownIcon}
						testId="assessment-results-status-filter"
					>
						{value === ASSESSMENT_RESULTS_STATUS_FILTER.PASSED ? (
							<Lozenge appearance="success">{formatMessage(messages.passedFilter)}</Lozenge>
						) : value === ASSESSMENT_RESULTS_STATUS_FILTER.NEEDS_ATTENTION ? (
							<Lozenge appearance="moved">{formatMessage(messages.needsAttentionFilter)}</Lozenge>
						) : value === ASSESSMENT_RESULTS_STATUS_FILTER.SKIPPED ? (
							<Lozenge appearance="neutral">{formatMessage(messages.skippedFilter)}</Lozenge>
						) : (
							formatMessage(messages.statusFilter)
						)}
					</Button>
				)}
			>
				<DropdownItemGroup>
					<DropdownItem
						isSelected={value === ASSESSMENT_RESULTS_STATUS_FILTER.PASSED}
						onClick={() => onChange(ASSESSMENT_RESULTS_STATUS_FILTER.PASSED)}
						testId="assessment-results-passed-filter"
					>
						<Lozenge appearance="success">{formatMessage(messages.passedFilter)}</Lozenge>
					</DropdownItem>
					<DropdownItem
						isSelected={value === ASSESSMENT_RESULTS_STATUS_FILTER.NEEDS_ATTENTION}
						onClick={() => onChange(ASSESSMENT_RESULTS_STATUS_FILTER.NEEDS_ATTENTION)}
						testId="assessment-results-needs-attention-filter"
					>
						<Lozenge appearance="moved">{formatMessage(messages.needsAttentionFilter)}</Lozenge>
					</DropdownItem>
					<DropdownItem
						isSelected={value === ASSESSMENT_RESULTS_STATUS_FILTER.SKIPPED}
						onClick={() => onChange(ASSESSMENT_RESULTS_STATUS_FILTER.SKIPPED)}
						testId="assessment-results-skipped-filter"
					>
						<Lozenge appearance="neutral">{formatMessage(messages.skippedFilter)}</Lozenge>
					</DropdownItem>
				</DropdownItemGroup>
			</DropdownMenu>
			{value && (
				<Button
					appearance="subtle"
					onClick={() => onChange(null)}
					testId="assessment-results-clear-filters"
				>
					{formatMessage(messages.clearFilters)}
				</Button>
			)}
		</Inline>
	);
};
